import { useEffect, useState } from 'react';
import type { Lead, TaskEntry, TaskListItem } from '@shared/types';
import { defaultTaskDue, formatDateTime, israelDateTime, israelToIso } from '@shared/dates';
import { useAddTask, useAllTasks, useCompleteTask, useDeleteTask, useStages, useUpdateTask } from '../store';

/** A click anywhere on a date field opens the native picker (not only its calendar icon, or one sub-field). */
const openPicker = (e: React.MouseEvent<HTMLInputElement>) => { try { e.currentTarget.showPicker(); } catch { /* unsupported: the field stays typeable */ } };

/** HH:MM on a 24-hour clock, every 5 minutes (a native time input follows the browser's 12/24h setting). */
const TIME_OPTIONS = Array.from({ length: 288 }, (_, i) => `${String(Math.floor(i / 12)).padStart(2, '0')}:${String((i % 12) * 5).padStart(2, '0')}`);
function TimeSelect({ value, onChange }: { value: string; onChange: (v: string) => void }) {
  const opts = value && !TIME_OPTIONS.includes(value) ? [...TIME_OPTIONS, value].sort() : TIME_OPTIONS; // an off-grid CRM time stays selectable
  return (
    <select className="input ltr w-28" value={value} onChange={(e) => onChange(e.target.value)} required aria-label="שעה">
      {opts.map((t) => <option key={t} value={t}>{t}</option>)}
    </select>
  );
}
import { Icons, Popover, Spinner } from './ui';

/**
 * CRM tasks. Piper's tasks are a view over the CRM's contact tasks (same ids): a task is created on the lead's contact
 * (the association is implied by where it was opened from), completing one completes it in the CRM, and tasks added in
 * the CRM show up here through the Task webhooks and the sweep.
 */

/** The lead row's tasks button (next to the notes bubble): open-task count; click opens the add-task form. */
export function TasksButton({ lead, card }: { lead: Lead; card?: { className: string; iconClassName: string } }) {
  const [anchor, setAnchor] = useState<HTMLButtonElement | null>(null);
  const [open, setOpen] = useState(false);
  const n = lead.openTaskCount;
  const label = n ? `${n} משימות פתוחות — הוסף משימה` : 'הוסף משימה';
  return (
    <>
      {card ? (
        // the mobile card: same size as its other actions, the count as a badge on the icon
        <button ref={setAnchor} type="button" className={`${card.className} relative`} title={label} aria-label={`משימות (${n})`}
          onClick={(e) => { e.stopPropagation(); setOpen(true); }}>
          <Icons.TaskCal className={card.iconClassName} />
          {n > 0 && <span className="absolute -end-1 -top-1 min-w-[18px] rounded-full bg-accent px-1 text-center text-[11px] font-bold leading-[18px] text-white">{n > 99 ? '99+' : n}</span>}
        </button>
      ) : (
        <button
          ref={setAnchor}
          type="button"
          className={`relative inline-flex h-11 min-w-11 items-center justify-center gap-1 rounded-full px-2 desk:h-8 desk:min-w-8 ${n ? 'bg-accent-soft text-accent' : 'text-ink-3 hover:bg-page'}`}
          title={label}
          aria-label={`משימות (${n})`}
          onClick={(e) => { e.stopPropagation(); setOpen(true); }}
        >
          <Icons.TaskCal size={15} />
          {n > 0 && <span className="text-xs font-semibold">{n}</span>}
        </button>
      )}
      <Popover open={open} anchor={anchor} onClose={() => setOpen(false)} title="משימה חדשה">
        <div className="w-80 max-w-full" onClick={(e) => e.stopPropagation()}>
          <TaskForm leadId={lead.id} onDone={() => setOpen(false)} autoFocus />
        </div>
      </Popover>
    </>
  );
}

/**
 * Title, description (removable), due date and time in Israel time, assignee (the creator by default).
 * With `task`, edits it: the fields start from the task, and saving writes all of them to the CRM.
 */
export function TaskForm({ leadId, task, onDone, autoFocus }: { leadId: string; task?: TaskEntry; onDone?: () => void; autoFocus?: boolean }) {
  const { meta } = useStages();
  const add = useAddTask();
  const update = useUpdateTask();
  const pending = false; // optimistic: the form never waits for the CRM
  const due = (task?.dueDate && israelDateTime(task.dueDate)) || defaultTaskDue();
  const [title, setTitle] = useState(task?.title ?? '');
  const [body, setBody] = useState(task?.body ?? '');
  const [withBody, setWithBody] = useState(!task || !!task.body);
  const [date, setDate] = useState(due.date);
  const [time, setTime] = useState(due.time);
  const [assignedTo, setAssignedTo] = useState(task?.assignedTo ?? meta?.me.ghlUserId ?? '');
  const dueIso = israelToIso(date, time);
  const ok = !!title.trim() && !!dueIso && !!assignedTo && !pending;

  const submit = () => {
    if (!ok) return;
    const fields = { title: title.trim(), ...(withBody && body.trim() ? { body: body.trim() } : {}), dueDate: dueIso!, assignedTo };
    // optimistic: the change shows right away and the form closes; an error puts the lists back and says why
    if (task) update.mutate({ taskId: task.id, leadId: task.contactId, task: fields });
    else { add.mutate({ id: leadId, task: fields }); setTitle(''); setBody(''); }
    onDone?.();
  };

  return (
    <form onSubmit={(e) => { e.preventDefault(); submit(); }} className="grid gap-3 text-sm">
      <label className="grid gap-1">
        <span className="text-xs font-medium text-ink-2">כותרת <span className="text-danger">*</span></span>
        <input className="input" value={title} onChange={(e) => setTitle(e.target.value)} maxLength={300} autoFocus={autoFocus} placeholder="מה צריך לעשות?" />
      </label>

      {withBody ? (
        <div className="grid gap-1">
          <button type="button" className="flex items-center gap-1 justify-self-start text-xs text-ink-2 hover:text-danger" onClick={() => setWithBody(false)}>⊖ הסר תיאור</button>
          <textarea className="input" rows={3} value={body} onChange={(e) => setBody(e.target.value)} maxLength={2000} placeholder="תיאור המשימה" />
          <span className="justify-self-end text-[11px] text-ink-3">{body.length} / 2000</span>
        </div>
      ) : (
        <button type="button" className="justify-self-start text-xs text-accent hover:underline" onClick={() => setWithBody(true)}>⊕ הוסף תיאור</button>
      )}

      <div className="grid gap-1">
        <span className="text-xs font-medium text-ink-2">מועד (שעון ישראל)</span>
        <div className="flex gap-2">
          <input className="input ltr flex-1" type="date" value={date} onChange={(e) => setDate(e.target.value)} onClick={openPicker} required aria-label="תאריך" />
          <TimeSelect value={time} onChange={setTime} />
        </div>
      </div>

      <label className="grid gap-1">
        <span className="text-xs font-medium text-ink-2">שיוך ל</span>
        <select className="input" value={assignedTo} onChange={(e) => setAssignedTo(e.target.value)}>
          {!assignedTo && <option value="">— בחר משתמש —</option>}
          {meta?.users.map((u) => <option key={u.id} value={u.id}>{u.name}{u.id === meta.me.ghlUserId ? ' (אני)' : ''}</option>)}
        </select>
      </label>

      <div className="flex justify-end gap-2">
        {onDone && <button type="button" className="btn btn-sm" onClick={onDone} disabled={pending}>ביטול</button>}
        <button type="submit" className="btn btn-primary btn-sm" disabled={!ok}>{pending ? <Spinner /> : 'שמור'}</button>
      </div>
    </form>
  );
}

const overdue = (t: TaskListItem) => !t.completed && !!t.dueDate && Date.parse(t.dueDate) < Date.now();

/** One task: checkbox (completes it in the CRM), title, description, deadline, assignee; optionally the lead. Edit and delete in place. */
function TaskRow({ t, showLead, onOpenLead }: { t: TaskListItem; showLead?: boolean; onOpenLead?: (id: string) => void }) {
  const { userName } = useStages();
  const complete = useCompleteTask();
  const del = useDeleteTask();
  const [mode, setMode] = useState<'view' | 'edit' | 'confirm-delete'>('view');
  const saving = t.id.startsWith('pending_'); // just added: waits for the CRM's id before it can be changed
  const opens = !!onOpenLead && t.isLead && mode === 'view';
  if (mode === 'edit') {
    return <li className="rounded-lg border border-accent/40 bg-panel p-3"><TaskForm leadId={t.contactId} task={t} onDone={() => setMode('view')} autoFocus /></li>;
  }
  return (
    <li className={`flex gap-2 rounded-lg border p-2 text-sm ${t.completed ? 'border-line bg-page opacity-70' : overdue(t) ? 'border-red-200 bg-red-50' : 'border-line bg-panel'} ${saving ? 'opacity-60' : ''}`}>
      <input type="checkbox" className="mt-1 h-4 w-4 shrink-0" checked={t.completed} disabled={saving} aria-label={t.completed ? 'סמן כפתוחה' : 'סמן כבוצעה'}
        onChange={(e) => complete.mutate({ taskId: t.id, leadId: t.contactId, completed: e.target.checked })} />
      <div className={`min-w-0 flex-1 ${opens ? 'cursor-pointer' : ''}`} onClick={opens ? () => onOpenLead!(t.contactId) : undefined}
        role={opens ? 'link' : undefined} title={opens ? 'פתח את הליד' : undefined}>
        <div className={`font-medium ${t.completed ? 'line-through' : ''}`} dir="auto">{t.title}</div>
        {t.body && <div className="mt-0.5 whitespace-pre-wrap break-words text-xs text-ink-2" dir="auto">{t.body}</div>}
        <div className="mt-1 flex flex-wrap items-center gap-x-3 gap-y-0.5 text-[11px] text-ink-3">
          <span className={overdue(t) ? 'font-semibold text-danger' : ''}><span className="ltr">{formatDateTime(t.dueDate)}</span>{overdue(t) ? ' · באיחור' : ''}</span>
          <span>{userName(t.assignedTo)}</span>
          {showLead && <span className={t.isLead ? 'text-accent' : ''} dir="auto">{t.isLead ? t.leadName || 'ליד' : t.leadName ?? 'איש קשר שאינו ליד'}</span>}
        </div>
        {mode === 'confirm-delete' && (
          // inline confirmation (no browser dialog): the task is deleted in the CRM too
          <div className="mt-2 flex flex-wrap items-center gap-2 rounded-md bg-red-50 p-2 text-xs text-danger" onClick={(e) => e.stopPropagation()}>
            <span>למחוק את המשימה? היא תימחק גם ב-CRM.</span>
            <button type="button" className="btn btn-sm border-red-300 text-danger"
              onClick={() => { setMode('view'); del.mutate({ taskId: t.id, leadId: t.contactId, open: !t.completed }); }}>מחק</button>
            <button type="button" className="btn btn-sm" onClick={() => setMode('view')}>ביטול</button>
          </div>
        )}
      </div>
      {mode === 'view' && !saving && (
        <div className="flex shrink-0 items-start gap-0.5">
          <button type="button" className="rounded p-1 text-ink-3 hover:bg-page hover:text-accent" title="ערוך משימה" aria-label="ערוך משימה" onClick={() => setMode('edit')}><Icons.EditDoc size={13} /></button>
          <button type="button" className="rounded p-1 text-ink-3 hover:bg-page hover:text-danger" title="מחק משימה" aria-label="מחק משימה" onClick={() => setMode('confirm-delete')}>✕</button>
        </div>
      )}
    </li>
  );
}

/** The lead's earliest open task, above its details (the list comes open-first, earliest deadline first). */
export function NextTask({ tasks, onShowAll }: { tasks: TaskListItem[] | undefined; onShowAll: () => void }) {
  const open = tasks?.filter((t) => !t.completed) ?? [];
  if (!open.length) return null;
  return (
    <section className="grid gap-1.5">
      <div className="flex items-center justify-between text-xs">
        <span className="flex items-center gap-1 font-medium text-ink-2"><Icons.TaskCal size={13} /> המשימה הבאה</span>
        {open.length > 1 && <button type="button" className="text-accent hover:underline" onClick={onShowAll}>כל המשימות ({open.length})</button>}
      </div>
      <ol><TaskRow t={open[0]} /></ol>
    </section>
  );
}

/** A lead's tasks (its detail tab): the add form on top, open tasks by deadline, then the done ones. */
export function LeadTasks({ leadId, tasks, loading }: { leadId: string; tasks: TaskListItem[] | undefined; loading: boolean }) {
  return (
    <div className="grid gap-4">
      <section className="card p-3"><TaskForm leadId={leadId} /></section>
      {loading ? <Spinner /> : !tasks?.length ? <p className="text-sm text-ink-3">אין משימות לליד הזה</p> : (
        <ol className="grid gap-2">{tasks.map((t) => <TaskRow key={t.id} t={t} />)}</ol>
      )}
    </div>
  );
}

type TaskTab = 'all' | 'today' | 'overdue' | 'upcoming';
type TaskStatus = 'open' | 'done' | 'all';
const TABS: Array<[TaskTab, string]> = [['all', 'הכל'], ['today', 'להיום'], ['overdue', 'באיחור'], ['upcoming', 'עתידיות']];
const STATUS: Array<[TaskStatus, string]> = [['open', 'פתוחות'], ['done', 'בוצעו'], ['all', 'הכל']];
/** Israel calendar day of an instant, "YYYY-MM-DD" (sorts as text). */
const dayOf = (iso: string | null) => (iso ? israelDateTime(iso)?.date ?? null : null);

/**
 * The tasks page: every task of the business. Tabs like the CRM's (all / due today / overdue / upcoming — by Israel
 * calendar day; the last three count open tasks only), one search over everything shown, then assignee, status and
 * sort by deadline. Clicking a task opens its lead.
 */
export function TasksPage({ onOpenLead }: { onOpenLead: (id: string) => void }) {
  const { meta, userName } = useStages();
  const { data, isLoading } = useAllTasks(true);
  const [tab, setTab] = useState<TaskTab>('all');
  const [status, setStatus] = useState<TaskStatus>('open');
  const mine = meta?.me.ghlUserId ?? '';
  const [assignee, setAssignee] = useState(mine); // '' any, '__none' unassigned, or a user id; my own tasks by default
  useEffect(() => { if (mine) setAssignee((a) => a || mine); }, [mine]); // meta arrived after the first render
  const [q, setQ] = useState('');
  const [desc, setDesc] = useState(false);

  const today = dayOf(new Date().toISOString())!;
  const inTab = (t: TaskListItem, which: TaskTab) => {
    if (which === 'all') return true;
    if (t.completed) return false;
    const d = dayOf(t.dueDate);
    if (which === 'today') return d === today;
    if (which === 'overdue') return overdue(t);
    return !!d && d > today; // upcoming
  };
  const query = q.trim().toLowerCase();
  const digits = query.replace(/\D/g, '');
  // the search covers the lead (name, phone, email), the assignee's name, the task's title and description, and its deadline as shown
  const matches = (t: TaskListItem) => {
    const text = [t.leadName, t.leadEmail, userName(t.assignedTo), t.title, t.body, formatDateTime(t.dueDate)].filter(Boolean).join('\n').toLowerCase();
    if (text.includes(query)) return true;
    const phone = (t.leadPhone ?? '').replace(/\D/g, '');
    return digits.length >= 3 && (phone.includes(digits) || phone.replace(/^972/, '0').includes(digits));
  };
  const filtered = (data ?? []).filter((t) => {
    if (status === 'open' && t.completed) return false;
    if (status === 'done' && !t.completed) return false;
    if (assignee === '__none' ? !!t.assignedTo : assignee && t.assignedTo !== assignee) return false;
    if (query && !matches(t)) return false;
    return true;
  });
  const counts = Object.fromEntries(TABS.map(([k]) => [k, filtered.filter((t) => inTab(t, k)).length])) as Record<TaskTab, number>;
  const rows = filtered.filter((t) => inTab(t, tab)).sort((a, b) => {
    // open before done; then by deadline (no deadline last), in the chosen direction
    if (a.completed !== b.completed) return a.completed ? 1 : -1;
    if (!a.dueDate || !b.dueDate) return a.dueDate ? -1 : b.dueDate ? 1 : 0;
    return desc ? b.dueDate.localeCompare(a.dueDate) : a.dueDate.localeCompare(b.dueDate);
  });
  const filtering = status !== 'open' || assignee !== mine || !!query || desc;
  const clear = () => { setStatus('open'); setAssignee(mine); setQ(''); setDesc(false); };

  return (
    <div className="mx-auto grid w-full max-w-4xl gap-3 p-4">
      <header className="flex items-center justify-between gap-2">
        <h1 className="flex items-center gap-2 text-lg font-bold"><Icons.TaskCal size={18} /> משימות</h1>
        <span className="text-sm text-ink-2">{rows.length} משימות</span>
      </header>

      <nav className="flex gap-1 overflow-x-auto border-b border-line [scrollbar-width:none]" role="tablist">
        {TABS.map(([k, label]) => (
          <button key={k} role="tab" aria-selected={tab === k} onClick={() => setTab(k)}
            className={`-mb-px flex shrink-0 items-center gap-1.5 border-b-2 px-3 py-2 text-sm ${tab === k ? 'border-accent font-semibold text-accent' : 'border-transparent text-ink-2'}`}>
            {label}
            <span className={`rounded-full px-1.5 text-[11px] ${k === 'overdue' && counts[k] ? 'bg-red-100 text-danger' : 'bg-page text-ink-3'}`}>{counts[k]}</span>
          </button>
        ))}
      </nav>

      <div className="relative">
        <Icons.Search size={14} className="pointer-events-none absolute inset-y-0 start-2.5 my-auto text-ink-3" />
        <input className="input w-full py-1.5 ps-8" type="search" value={q} onChange={(e) => setQ(e.target.value)}
          placeholder="חיפוש: ליד, טלפון, אימייל, אחראי, כותרת, תיאור, תאריך" aria-label="חיפוש משימות" />
      </div>

      <div className="flex items-end gap-1.5 text-xs desk:gap-2 desk:text-sm">
        <label className="grid min-w-0 flex-1 gap-0.5 desk:flex-none">
          <span className="text-[11px] text-ink-3">אחראי</span>
          <select className="input h-9 min-h-0 w-full min-w-0 px-2 py-1 text-xs desk:h-auto desk:py-1.5 desk:text-sm" value={assignee} onChange={(e) => setAssignee(e.target.value)}>
            <option value="">כל האחראים</option>
            {meta?.me.ghlUserId && <option value={meta.me.ghlUserId}>המשימות שלי</option>}
            <option value="__none">ללא אחראי</option>
            {meta?.users.map((u) => <option key={u.id} value={u.id}>{u.name}</option>)}
          </select>
        </label>
        <label className="grid min-w-0 flex-1 gap-0.5 desk:flex-none">
          <span className="text-[11px] text-ink-3">סטטוס</span>
          <select className="input h-9 min-h-0 w-full min-w-0 px-2 py-1 text-xs desk:h-auto desk:py-1.5 desk:text-sm" value={status} onChange={(e) => setStatus(e.target.value as TaskStatus)}>
            {STATUS.map(([k, label]) => <option key={k} value={k}>{label}</option>)}
          </select>
        </label>
        <label className="grid min-w-0 flex-1 gap-0.5 desk:flex-none">
          <span className="text-[11px] text-ink-3">מיון מועד</span>
          <select className="input h-9 min-h-0 w-full min-w-0 px-2 py-1 text-xs desk:h-auto desk:py-1.5 desk:text-sm" value={desc ? 'desc' : 'asc'} onChange={(e) => setDesc(e.target.value === 'desc')}>
            <option value="asc">המוקדם קודם</option>
            <option value="desc">המאוחר קודם</option>
          </select>
        </label>
        {filtering && <button type="button" className="btn btn-sm shrink-0 px-2 text-xs" onClick={clear}>נקה</button>}
      </div>

      {isLoading ? <div className="flex justify-center p-6"><Spinner /></div> : !rows.length ? <p className="p-6 text-center text-sm text-ink-3">אין משימות</p> : (
        <ol className="grid gap-2">{rows.map((t) => <TaskRow key={t.id} t={t} showLead onOpenLead={onOpenLead} />)}</ol>
      )}
    </div>
  );
}
